import React, { Fragment, useState, useEffect } from 'react';
import { Form, Input, Select, Button, Tooltip, Space, Switch, Flex, Grid } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useAuthSettingUtils } from '../../helpers/settings/settingUtils';
import api from '../../services/api/';

const DEFAULT_CONTACT_NAMES = [
  { value: 'Mobile', icon: '📱' },
  { value: 'Home', icon: '🏠' },
  { value: 'Work', icon: '💼' },
  { value: 'Office', icon: '🏢' },
  { value: 'Business', icon: '📞' },
  { value: 'Fax', icon: '📠' },
  { value: 'Other', icon: '☎️' }
];

const { useBreakpoint } = Grid;

/** Stable component so input keeps focus when parent re-renders (must be outside ContactNumbers) */
const ContactNumberRowInput = React.forwardRef((props, ref) => {
  const {
    value = '',
    onChange,
    fieldIndex,
    fieldName,
    countryCodes,
    countryIds,
    defaultCountryCode,
    defaultCountryId,
    setNationalNumbers,
    combineContactNumber,
    form,
    getAuthSetting,
    field,
    handleTypeChange,
    loadingCountries,
    filterCountryOption,
    countryOptions,
    handleCountryChange,
    typeSelectWidth = 180,
    countrySelectWidth = 200,
  } = props;
  return (
    <Space.Compact style={{ display: 'flex', width: '100%' }}>
      <Form.Item
        name={[field.name, 'name']}
        rules={[{ required: true, message: 'Type required' }]}
        noStyle
      >
        <Select
          placeholder="Type"
          style={{ width: typeSelectWidth }}
          dropdownStyle={{ minWidth: 220 }}
          onChange={(val) => handleTypeChange(field.key, val)}
          options={DEFAULT_CONTACT_NAMES.map(item => ({
            value: item.value,
            label: `${item.icon} ${item.value}`
          }))}
        />
      </Form.Item>
      <Select
        showSearch
        placeholder="Select country"
        style={{ width: countrySelectWidth }}
        value={String(countryIds[fieldIndex] ?? defaultCountryId)}
        onChange={(val) => handleCountryChange(fieldIndex, val)}
        loading={loadingCountries}
        listHeight={400}
        dropdownStyle={{ minWidth: 260 }}
        filterOption={filterCountryOption}
        optionLabelProp="label"
        notFoundContent={loadingCountries ? 'Loading...' : 'No country found'}
        getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
      >
        {countryOptions.map((option, optIndex) => {
          const country = option.country;
          const optionKey = country.id ? `country-${country.id}` : `country-${option.value}-${optIndex}`;
          return (
            <Select.Option
              key={optionKey}
              value={String(option.value)}
              country={country}
              data-search-text={option.searchText}
              label={
                <span>
                  {country.emoji && <span style={{ marginRight: 6 }}>{country.emoji}</span>}
                  <span>{country.phone_code}</span>
                </span>
              }
            >
              {option.displayLabel}
            </Select.Option>
          );
        })}
      </Select>
      <Form.Item name={[field.name, 'contact_country_id']} noStyle style={{ display: 'none' }}>
        <Input type="hidden" />
      </Form.Item>
      <Input
        ref={ref}
        placeholder={getAuthSetting('format_contact_number')}
        maxLength={15}
        style={{ flex: 1, minWidth: 80 }}
        value={value}
        onChange={(e) => {
          const digits = (e.target.value || '').replace(/\D/g, '');
          setNationalNumbers(prev => ({ ...prev, [fieldIndex]: digits }));
          const code = countryCodes[fieldIndex] || defaultCountryCode;
          const combined = digits === '' ? '' : combineContactNumber(code, digits);
          const countryId = countryIds[fieldIndex] || defaultCountryId;
          if (countryId) {
            form.setFieldValue([fieldName, fieldIndex, 'contact_country_id'], countryId);
          }
          onChange?.(combined);
        }}
      />
    </Space.Compact>
  );
});

ContactNumberRowInput.displayName = 'ContactNumberRowInput';

const ContactNumbers = ({ 
  name = "contact_numbers",
  initialValues = null,
  onChange = null,
  showAsFormList = true,
  /** When false, an empty number is allowed (e.g. customer form when org setting is off). */
  requireContactNumber = true,
}) => {
  const screens = useBreakpoint();
  const isSmallScreen = !screens.md;
  const typeSelectWidth = isSmallScreen ? 70 : 180;
  const countrySelectWidth = isSmallScreen ? 90 : 200;

  const [fieldValues, setFieldValues] = useState({});
  const [defaultField, setDefaultField] = useState(0);
  const [countryCodes, setCountryCodes] = useState({});
  const [countryIds, setCountryIds] = useState({});
  const [nationalNumbers, setNationalNumbers] = useState({});
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const form = Form.useFormInstance();
  const { getAuthSetting, getAuthOrganization } = useAuthSettingUtils();
  const defaultCountryCode = getAuthOrganization()?.country?.phone_code || '98';
  const defaultCountryId = getAuthOrganization()?.country?.id || null;

  // Parse contact number from format +989121234567 to {countryCode: '98', nationalNumber: '9121234567'}
  // fieldIndex is optional - if provided, uses that field's country code as a hint
  const parseContactNumber = (contactNumber, fieldIndex = null) => {
    if (!contactNumber) {
      const code = fieldIndex !== null ? (countryCodes[fieldIndex] || defaultCountryCode) : defaultCountryCode;
      return { countryCode: code, nationalNumber: '' };
    }
    
    // Remove all non-digit characters except +
    let cleaned = contactNumber.toString().trim();
    
    // If starts with +, extract country code and national number
    if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1); // Remove +
      const digits = cleaned.replace(/\D/g, '');
      
      if (!digits) {
        const code = fieldIndex !== null ? (countryCodes[fieldIndex] || defaultCountryCode) : defaultCountryCode;
        return { countryCode: code, nationalNumber: '' };
      }
      
      // Get the expected country code for this field (if fieldIndex provided)
      const expectedCode = fieldIndex !== null ? (countryCodes[fieldIndex] || defaultCountryCode) : null;
      
      // Get all unique phone codes from countries list, sorted by length (longest first)
      const phoneCodes = countries.length > 0 
        ? [...new Set(countries.map(c => c.phone_code))].sort((a, b) => b.length - a.length)
        : [defaultCountryCode];
      
      // Try to match country codes from the countries list
      let countryCode = defaultCountryCode;
      let nationalNumber = digits;
      
      // First, if we have an expected code for this field, try it first
      if (expectedCode && digits.startsWith(expectedCode)) {
        const remaining = digits.substring(expectedCode.length);
        // If remaining is reasonable length (at least 4 digits for a valid phone number)
        if (remaining.length >= 4 && remaining.length <= 15) {
          countryCode = expectedCode;
          nationalNumber = remaining;
          return { countryCode, nationalNumber };
        }
      }
      
      // Then try exact matches from countries list
      for (const code of phoneCodes) {
        if (digits.startsWith(code)) {
          const remaining = digits.substring(code.length);
          // If remaining is reasonable length (4-15 digits), this is likely the country code
          if (remaining.length >= 4 && remaining.length <= 15) {
            countryCode = code;
            nationalNumber = remaining;
            break;
          }
        }
      }
      
      // If no match found, try default country code
      if (nationalNumber === digits && digits.startsWith(defaultCountryCode)) {
        const remaining = digits.substring(defaultCountryCode.length);
        if (remaining.length >= 4) {
          countryCode = defaultCountryCode;
          nationalNumber = remaining;
        }
      }
      
      return { countryCode, nationalNumber };
    }
    
    // If no +, assume it's already a national number (no country code)
    const digits = cleaned.replace(/\D/g, '');
    const code = fieldIndex !== null ? (countryCodes[fieldIndex] || defaultCountryCode) : defaultCountryCode;
    return { countryCode: code, nationalNumber: digits };
  };

  // Combine country code and national number to format +[country code][national number]
  const combineContactNumber = (countryCode, nationalNumber) => {
    const code = (countryCode || defaultCountryCode).replace(/\D/g, '');
    const number = (nationalNumber || '').replace(/\D/g, '');
    if (!code && !number) return '';
    return `+${code}${number}`;
  };

  // Track if we've initialized to prevent re-initialization loops
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [hasReparsedAfterCountriesLoad, setHasReparsedAfterCountriesLoad] = React.useState(false);

  // Fetch countries list
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const response = await api.get('/countries');
        const countriesData = response.data.data || [];
        // Sort countries by name
        const sortedCountries = countriesData.sort((a, b) => a.name.localeCompare(b.name));
        setCountries(sortedCountries);
      } catch (error) {
        console.error('Error fetching countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  // Helper function to get phone_code from country_id
  const getPhoneCodeFromCountryId = (countryId) => {
    if (!countryId) return defaultCountryCode;
    const country = countries.find(c => String(c.id) === String(countryId));
    return country ? String(country.phone_code) : defaultCountryCode;
  };

  // Re-parse contact numbers when countries are loaded (only if we need to get phone_code from country_id)
  useEffect(() => {
    if (countries.length === 0 || !isInitialized || hasReparsedAfterCountriesLoad) return;
    
    // Only re-parse if we have country_ids but need to update phone_codes
    const currentValues = form.getFieldValue(name);
    if (!currentValues || !Array.isArray(currentValues)) {
      setHasReparsedAfterCountriesLoad(true);
      return;
    }
    
    let needsUpdate = false;
    const updatedCodes = { ...countryCodes };
    
    currentValues.forEach((item, index) => {
      if (item.contact_country_id && !countryCodes[index]) {
        // We have contact_country_id but no phone_code yet - get it from countries list
        const phoneCode = getPhoneCodeFromCountryId(item.contact_country_id);
        updatedCodes[index] = phoneCode;
        needsUpdate = true;
      }
    });
    
    if (needsUpdate) {
      setCountryCodes(updatedCodes);
    }
    
    setHasReparsedAfterCountriesLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countries.length, isInitialized]);

  // Sync hidden form fields with combined values when national numbers or country codes change
  useEffect(() => {
    if (!isInitialized) return;
    
    const currentValues = form.getFieldValue(name);
    if (currentValues && Array.isArray(currentValues)) {
      // Update each field's hidden values with combined format and country info
      currentValues.forEach((item, index) => {
        const national = nationalNumbers[index] || '';
        const code = countryCodes[index] || defaultCountryCode;
        const countryId = countryIds[index] || defaultCountryId;
        const combined = combineContactNumber(code, national);
        
        // Update if different from current value
        if (item.contact_number !== combined) {
          form.setFieldValue([name, index, 'contact_number'], combined);
        }
        if (item.contact_country_id !== countryId && countryId) {
          form.setFieldValue([name, index, 'contact_country_id'], countryId);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationalNumbers, countryCodes, countryIds, isInitialized]);

  // Provide combined values via onChange callback for submission (if onChange prop is provided)
  useEffect(() => {
    if (!onChange || !isInitialized) return;
    
    // Use a timeout to debounce and avoid infinite loops
    const timeoutId = setTimeout(() => {
      const currentValues = form.getFieldValue(name);
      if (currentValues && Array.isArray(currentValues)) {
        // Values are already combined in the form, so just pass them
        onChange(currentValues);
      }
    }, 100);
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nationalNumbers, countryCodes, isInitialized]);

  // Ensure form is properly initialized with default values
  useEffect(() => {
    if (isInitialized) return; // Only initialize once
    
    // Priority: initialValues > form.getFieldValue > default values
    let valuesToProcess = null;
    
    if (initialValues && Array.isArray(initialValues) && initialValues.length > 0) {
      // Use initialValues if provided
      valuesToProcess = initialValues;
    } else {
      // Otherwise check form values
      const currentValues = form.getFieldValue(name);
      if (currentValues && Array.isArray(currentValues) && currentValues.length > 0) {
        valuesToProcess = currentValues;
      }
    }
    
    if (!valuesToProcess || valuesToProcess.length === 0) {
      // No values, use defaults
      const defaultValues = getInitialValue();
      const initialCodes = {};
      const initialIds = {};
      const initialNationals = {};
      const formValues = [];
      defaultValues.forEach((item, index) => {
        const parsed = parseContactNumber(item.contact_number, index);
        initialCodes[index] = parsed.countryCode;
        initialNationals[index] = parsed.nationalNumber;
        const combined = combineContactNumber(parsed.countryCode, parsed.nationalNumber);
        
        // Set default contact_country_id
        const countryId = defaultCountryId;
        initialIds[index] = countryId;
        
        formValues.push({
          ...item,
          contact_number: combined,
          contact_country_id: countryId
        });
      });
      setCountryCodes(initialCodes);
      setCountryIds(initialIds);
      setNationalNumbers(initialNationals);
      form.setFieldsValue({
        [name]: formValues
      });
    } else {
      // Use contact_country_id and extra.contact_country_phone_code directly from response
      const parsedCodes = {};
      const parsedIds = {};
      const parsedNationals = {};
      const formValues = [];
      
      valuesToProcess.forEach((item, index) => {
        // Get contact_country_id directly from response, use default if not provided
        let countryId = item.contact_country_id || null;
        if (!countryId) {
          countryId = defaultCountryId;
        }
        parsedIds[index] = countryId;
        
        // Get phone_code from extra.contact_country_phone_code (remove any "+" prefix) or find from countries list
        let phoneCode = item.extra?.contact_country_phone_code || null;
        // Remove "+" prefix if present
        if (phoneCode && typeof phoneCode === 'string') {
          phoneCode = phoneCode.replace(/^\+/, '');
        }
        
        if (!phoneCode && countries.length > 0) {
          phoneCode = getPhoneCodeFromCountryId(countryId);
        } else if (!phoneCode) {
          phoneCode = defaultCountryCode;
        }
        phoneCode = String(phoneCode);
        parsedCodes[index] = phoneCode;
        
        if (item.contact_number) {
          // Extract national number using the known phone_code
          let nationalNumber = '';
          let contactNumberStr = item.contact_number.toString().trim();
          
          // Remove + prefix if present
          if (contactNumberStr.startsWith('+')) {
            contactNumberStr = contactNumberStr.substring(1);
          }
          
          // Remove all non-digits
          const digits = contactNumberStr.replace(/\D/g, '');
          
          // If we have phone_code, remove it from the beginning of digits
          if (phoneCode && digits.startsWith(phoneCode)) {
            nationalNumber = digits.substring(phoneCode.length);
          } else {
            // Fallback to parsing if phone_code doesn't match
            const parsed = parseContactNumber(item.contact_number, index);
            nationalNumber = parsed.nationalNumber;
          }
          
          parsedNationals[index] = nationalNumber;
          const combined = combineContactNumber(phoneCode, nationalNumber);
          
          formValues.push({
            ...item,
            contact_number: combined,
            contact_country_id: countryId
          });
        } else {
          parsedNationals[index] = '';
          const combined = combineContactNumber(phoneCode, '');
          formValues.push({
            ...item,
            contact_number: combined,
            contact_country_id: countryId
          });
        }
      });
      
      setCountryCodes(parsedCodes);
      setCountryIds(parsedIds);
      setNationalNumbers(parsedNationals);
      form.setFieldsValue({
        [name]: formValues
      });
    }
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, form, initialValues]);

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/\D/g, '');
    const phoneNumberLength = phoneNumber.length;
    
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleTypeChange = (fieldKey, value) => {
    setFieldValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  const handleDefaultChange = (fieldIndex) => {
    setDefaultField(fieldIndex);
    
    // Update all other fields' is_default to false (current field is handled by Form.Item)
    const currentValues = form.getFieldValue(name) || [];
    currentValues.forEach((item, index) => {
      if (index !== fieldIndex) {
        form.setFieldValue([name, index, 'is_default'], false);
      }
    });
  };

  const getInitialValue = () => {
    if (initialValues && Array.isArray(initialValues) && initialValues.length > 0) {
      // Parse contact numbers and extract country codes
      const parsedValues = initialValues.map((item, index) => {
        const parsed = parseContactNumber(item.contact_number, index);
        // Return with combined value in form field (country code + national number)
        const combined = combineContactNumber(parsed.countryCode, parsed.nationalNumber);
        return {
          ...item,
          contact_number: combined, // Combined format for hidden form field
          is_default: item.is_default !== undefined ? item.is_default : (index === 0)
        };
      });
      
      // If only one contact, ensure it's default
      if (parsedValues.length === 1) {
        parsedValues[0].is_default = true;
      }
      return parsedValues;
    }
    // Always return at least one default contact if no initial values
    const initialCode = defaultCountryCode;
    // Return empty contact_number, it will be set on blur
    return [{ name: 'Mobile', contact_number: '', is_default: true }];
  };

  // Handle country selection change
  const handleCountryChange = (fieldIndex, countryId) => {
    // countryId is now the country.id (not phone_code)
    const country = countries.find(c => String(c.id) === String(countryId));
    
    if (!country) {
      // Fallback to default if country not found
      const code = String(defaultCountryCode);
      setCountryCodes(prev => ({ ...prev, [fieldIndex]: code }));
      setCountryIds(prev => ({ ...prev, [fieldIndex]: defaultCountryId }));
      form.setFieldValue([name, fieldIndex, 'contact_country_id'], defaultCountryId);
      const national = nationalNumbers[fieldIndex] || '';
      const combined = combineContactNumber(code, national);
      form.setFieldValue([name, fieldIndex, 'contact_number'], combined);
      return;
    }
    
    const code = String(country.phone_code);
    const id = country.id;
    
    setCountryCodes(prev => ({ ...prev, [fieldIndex]: code }));
    setCountryIds(prev => ({ ...prev, [fieldIndex]: id }));
    
    // Update hidden form field
    form.setFieldValue([name, fieldIndex, 'contact_country_id'], id);
    
    // Update hidden form field with combined value (new country code + existing national number)
    const national = nationalNumbers[fieldIndex] || '';
    const combined = combineContactNumber(code, national);
    form.setFieldValue([name, fieldIndex, 'contact_number'], combined);
  };

  // Get country by phone code
  const getCountryByPhoneCode = (phoneCode) => {
    return countries.find(country => country.phone_code === phoneCode) || null;
  };

  // Get country options for Select - memoized to prevent re-renders
  // Use country.id as value to uniquely identify countries (even if they share phone_code)
  const countryOptions = React.useMemo(() => {
    return countries.map(country => ({
      value: String(country.id), // Use country ID as value for unique identification
      phoneCode: String(country.phone_code), // Store phone code separately
      label: country.name, // Simple label for search
      // Pre-computed searchable text for better performance
      searchText: `${country.name} ${country.phone_code} ${country.iso2 || ''}`.toLowerCase(),
      displayLabel: (
        <span>
          {/* {country.iso2 && <span style={{ marginRight: 8 }}>{country.iso2}</span>} */}
          <span style={{ marginRight: 8 }}>+{country.phone_code}</span>
          <span>{country.name}</span>
        </span>
      ),
      country: country
    }));
  }, [countries]);

  // Memoized filter function - optimized for performance with early returns
  const filterCountryOption = React.useCallback((input, option) => {
    if (!input) return true;
    
    const searchText = input.toLowerCase();
    
    // Try to get search text from data attribute first (fastest)
    const searchTextAttr = option?.props?.['data-search-text'];
    if (searchTextAttr) {
      return searchTextAttr.includes(searchText);
    }
    
    // Fallback to country properties
    const country = option?.props?.country;
    if (country) {
      return (
        country.name.toLowerCase().includes(searchText) ||
        country.phone_code.includes(searchText) ||
        (country.iso2 && country.iso2.toLowerCase().includes(searchText))
      );
    }
    
    // Last resort: search in option value
    return String(option?.value || '').toLowerCase().includes(searchText);
  }, []);

  // Handle national number change (kept for backward compatibility, but onChange now handles it directly)
  const handleNationalNumberChange = (fieldIndex, value) => {
    // Extract only digits (no country code should be in the input)
    const nationalNumber = value.replace(/\D/g, '');
    
    // Update state for display (only national number)
    setNationalNumbers(prev => ({ ...prev, [fieldIndex]: nationalNumber }));
    
    // Get current country code for this field
    const countryCode = countryCodes[fieldIndex] || defaultCountryCode;
    
    // Combine country code + national number for storage
    const combined = combineContactNumber(countryCode, nationalNumber);
    form.setFieldValue([name, fieldIndex, 'contact_number'], combined);
  };

  // if (!showAsFormList && onChange) {
  //   // For components that need custom state management (like CustomerContactNumber)
  //   // This would be a separate implementation
  //   return null; // Placeholder for now
  // }

  // Memoize initial value to prevent re-renders
  // Only recalculate if initialValues actually changes (by stringifying for deep comparison)
  const initialValuesKey = React.useMemo(() => 
    initialValues ? JSON.stringify(initialValues) : 'empty', 
    [initialValues]
  );
  const memoizedInitialValue = React.useMemo(() => getInitialValue(), [initialValuesKey, defaultCountryCode]);

  return (
    <Form.List 
      name={name}
      initialValue={memoizedInitialValue}
    >
      {(fields, { add, remove }) => (
        <Fragment>
          {fields.map((field, index) => {
            const currentType = fieldValues[field.key] || 'Mobile';
            const currentValues = form.getFieldValue(name) || [];
            const isDefault = currentValues[index]?.is_default || false;
            
            return (
              <div key={`contact-${field.key}-${index}`} className="mb-3">
                <Flex  justify="space-between">
                  <Flex align="flex-end" justify="space-between" className="mb-2">
                    <div style={{width: '120px'}}>
                    {isDefault ? <small className="text-muted me-2">Contact {index + 1} is default</small> : <small className="text-muted me-2">Set contact {index + 1} as default</small>}
                    </div>
                    
                    <Form.Item
                      name={[field.name, 'is_default']}
                      noStyle
                      valuePropName="checked"
                    >
                      <Switch 
                        size="small"
                        onChange={(checked) => {
                          if (checked) {
                            handleDefaultChange(index);
                          }
                        }}
                      />
                    </Form.Item>
                  </Flex>
                  <div className="">
                  {fields.length > 1 && (
                    <Tooltip title="Remove contact">
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          remove(field.name);
                          setFieldValues(prev => {
                            const newValues = { ...prev };
                            delete newValues[field.key];
                            return newValues;
                          });
                          setCountryCodes(prev => {
                            const newCodes = { ...prev };
                            delete newCodes[index];
                            // Reindex remaining codes
                            const reindexed = {};
                            Object.keys(newCodes).forEach(key => {
                              const keyNum = parseInt(key);
                              if (keyNum > index) {
                                reindexed[keyNum - 1] = newCodes[key];
                              } else if (keyNum < index) {
                                reindexed[key] = newCodes[key];
                              }
                            });
                            return reindexed;
                          });
                          setCountryIds(prev => {
                            const newIds = { ...prev };
                            delete newIds[index];
                            // Reindex remaining ids
                            const reindexed = {};
                            Object.keys(newIds).forEach(key => {
                              const keyNum = parseInt(key);
                              if (keyNum > index) {
                                reindexed[keyNum - 1] = newIds[key];
                              } else if (keyNum < index) {
                                reindexed[key] = newIds[key];
                              }
                            });
                            return reindexed;
                          });
                          setNationalNumbers(prev => {
                            const newNationals = { ...prev };
                            delete newNationals[index];
                            // Reindex remaining numbers
                            const reindexed = {};
                            Object.keys(newNationals).forEach(key => {
                              const keyNum = parseInt(key);
                              if (keyNum > index) {
                                reindexed[keyNum - 1] = newNationals[key];
                              } else if (keyNum < index) {
                                reindexed[key] = newNationals[key];
                              }
                            });
                            return reindexed;
                          });
                          if (index === defaultField && fields.length > 1) {
                            setDefaultField(0);
                          } else if (index < defaultField) {
                            setDefaultField(defaultField - 1);
                          }
                        }}
                      />
                    </Tooltip>
                  )}
                  </div>
                </Flex>
                
                {/* Form.Item wraps entire compact so error message appears at bottom of row */}
                <Form.Item
                  name={[field.name, 'contact_number']}
                  rules={[
                    {
                      validator: (_, value) => {
                        const digits = !value ? '' : String(value).replace(/\D/g, '');
                        const code = countryCodes[index] || defaultCountryCode;
                        const codeDigits = String(code).replace(/\D/g, '');
                        const national = codeDigits && digits.startsWith(codeDigits)
                          ? digits.slice(codeDigits.length)
                          : digits;
                        if (!requireContactNumber) {
                          if (!national || national.length === 0) {
                            return Promise.resolve();
                          }
                          if (national.length < 7) {
                            return Promise.reject(new Error('Phone number too short'));
                          }
                          return Promise.resolve();
                        }
                        if (!digits) {
                          return Promise.reject(new Error('Contact number required'));
                        }
                        if (national.length < 7) {
                          return Promise.reject(new Error('Phone number too short'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                  getValueProps={(value) => {
                    if (!value || !String(value).trim()) return { value: '' };
                    const code = countryCodes[index] || defaultCountryCode;
                    const codeDigits = String(code).replace(/\D/g, '');
                    const digits = String(value).replace(/\D/g, '').replace(/^\+/, '');
                    const national = codeDigits && digits.startsWith(codeDigits)
                      ? digits.slice(codeDigits.length)
                      : digits;
                    if (national.length === 0) return { value: '' };
                    return { value: formatPhoneNumber(national) };
                  }}
                  style={{ marginBottom: 0 }}
                >
                  <ContactNumberRowInput
                    fieldIndex={index}
                    fieldName={name}
                    countryCodes={countryCodes}
                    countryIds={countryIds}
                    defaultCountryCode={defaultCountryCode}
                    defaultCountryId={defaultCountryId}
                    setNationalNumbers={setNationalNumbers}
                    combineContactNumber={combineContactNumber}
                    form={form}
                    getAuthSetting={getAuthSetting}
                    field={field}
                    handleTypeChange={handleTypeChange}
                    loadingCountries={loadingCountries}
                    filterCountryOption={filterCountryOption}
                    countryOptions={countryOptions}
                    handleCountryChange={handleCountryChange}
                    typeSelectWidth={typeSelectWidth}
                    countrySelectWidth={countrySelectWidth}
                  />
                </Form.Item>
              </div>
            );
          })}

          <Form.Item>
            <Button
              type="link"
              size='small'
              onClick={() => {
                const currentFields = fields.length;
                const newIndex = currentFields;
                const newCountryCode = String(defaultCountryCode);
                const newCountryId = defaultCountryId;
                setCountryCodes(prev => ({ ...prev, [newIndex]: newCountryCode }));
                setCountryIds(prev => ({ ...prev, [newIndex]: newCountryId }));
                setNationalNumbers(prev => ({ ...prev, [newIndex]: '' }));
                add({ 
                  name: 'Mobile', 
                  contact_number: '', 
                  contact_country_id: newCountryId,
                  is_default: currentFields === 0 // Set as default if it's the first contact
                });
              }}
              icon={<PlusOutlined />}
              className='px-0'
            >
              Add Contact Number
            </Button>
          </Form.Item>

        </Fragment>
      )}
    </Form.List>
  );
};

export default ContactNumbers;
